<template>
  <div class="dashboard-container">
    <!-- 3D标题效果 -->
    <div class="title-container">
      <h1 class="title-3d" style="display: flex; justify-content: center">
        农情在线监测系统
        <div v-if="tabsData.activeTab > 0">
          - {{ tabsData.tabs[tabsData.activeTab].name }}
        </div>
      </h1>
      <div class="title-shadow"></div>
    </div>

    <!-- 时间显示 -->
    <div class="time-display">
      <div class="time">{{ currentTime }}</div>
      <div class="date">{{ currentDate }}</div>
    </div>

    <!-- 顶部页签导航 -->
    <div
      class="tab-container"
      style="display: flex; justify-content: center; margin-bottom: 10px"
    >
      <div
        v-for="(tab, index) in tabsData.tabs"
        :key="index"
        class="tab-item"
        :class="{ active: tabsData.activeTab === index }"
        @click="tabChange(index, tab.name)"
      >
        <div class="button">
          <i :class="tab.icon"></i>
          {{ tab.name }}
        </div>
      </div>
    </div>

    <div v-if="tabsData.activeTab === 0">
      <!-- v-if="activeTab === 0"-->
      <!-- 数据卡片行 -->
      <div class="data-cards">
        <div v-for="(card, index) in cards" :key="index" class="card">
          <div class="card-icon" :style="{ backgroundColor: card.color }">
            <i :class="card.icon"></i>
          </div>
          <div class="card-content">
            <div class="card-title">{{ card.title }}</div>
            <div class="card-value" :style="{ color: card.color }">
              {{ card.value }}
            </div>
            <div class="card-trend">
              <span :style="{ color: card.trend > 0 ? '#f56c6c' : '#67c23a' }">
                {{ card.trend > 0 ? "↑" : "↓" }} {{ Math.abs(card.trend) }}%
              </span>
              同比
            </div>
          </div>
        </div>
      </div>

      <!-- 主内容区 -->
      <div class="main-content">
        <!-- 左侧图表区 -->
        <div class="left-section">
          <div class="chart-container">
            <div class="chart-title">访问量趋势</div>
            <div ref="lineChart" class="chart"></div>
          </div>
          <div class="chart-container">
            <div class="chart-title">用户分布</div>
            <div ref="pieChart" class="chart"></div>
          </div>
        </div>

        <!-- 中间表格区 -->
        <div class="center-section">
          <div class="table-container">
            <div class="table-title">实时数据监控</div>
            <el-table
              :data="tableData"
              style="width: 100%"
              height="100%"
              :header-cell-style="{ background: '#1a3a6e', color: '#fff' }"
              :cell-style="{ background: '#0f2d5d', color: '#fff' }"
              stripe
            >
              <el-table-column
                prop="id"
                label="ID"
                width="40"
              ></el-table-column>
              <el-table-column
                prop="name"
                label="名称"
                width="120"
              ></el-table-column>
              <el-table-column prop="status" label="状态" width="70">
                <template #default="scope">
                  <el-tag
                    :type="scope.row.status === '正常' ? 'success' : 'danger'"
                  >
                    {{ scope.row.status }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column
                prop="value"
                label="数值"
                width="80"
              ></el-table-column>
              <el-table-column prop="rate" label="增长率" width="120">
                <template #default="scope">
                  <span
                    :style="{
                      color: scope.row.rate > 0 ? '#f56c6c' : '#67c23a',
                    }"
                  >
                    {{ scope.row.rate > 0 ? "↑" : "↓" }}
                    {{ Math.abs(scope.row.rate) }}%
                  </span>
                </template>
              </el-table-column>
              <el-table-column prop="time" label="更新时间"></el-table-column>
            </el-table>
          </div>
        </div>

        <!-- 右侧图表区 -->
        <div class="right-section">
          <div class="chart-container">
            <div class="chart-title">任务完成率</div>
            <div ref="gaugeChart" class="chart"></div>
          </div>
          <div class="chart-container">
            <div class="chart-title">资源使用</div>
            <div ref="barChart" class="chart"></div>
          </div>
        </div>
      </div>
    </div>

    <div v-else>
      <!-- <div class="table-container"> -->
      <el-table
        :data="filteredTableData"
        style="width: 100%"
        :header-cell-style="{ background: '#1a3a6e', color: '#fff' }"
        :cell-style="{ background: '#0f2d5d', color: '#fff' }"
        stripe
        height="79vh"
      >
        <el-table-column
          v-for="(item, index) in tableDataList.title"
          :key="index"
          :label="item.name"
          :min-width="item.width || 100"
        >
          <template #default="scope">
            {{ scope.row.data[index] }}
          </template>
        </el-table-column>
      </el-table>
      <!-- </div> -->
    </div>

    <!-- 底部滚动信息 -->
    <div class="scroll-info">
      <div
        class="scroll-content"
        :style="{ transform: `translateX(${scrollPosition}px)` }"
      >
        {{ scrollText }} • {{ scrollText }} • {{ scrollText }}
      </div>
    </div>
  </div>
</template>

<script setup>
import * as echarts from "echarts";
import { ref, onMounted, onBeforeUnmount, computed } from "vue";
import { debounce } from "lodash";
import { nextTick } from "vue";

const tableDataList = ref({
  title: [
    { name: "序号", width: 50 },
    { name: "设备类型名称", width: 200 },
    { name: "设备类型编码" },
    { name: "设备SN码", width: 150 },
    { name: "在线状态" },
    { name: "上传数据名称" },
    { name: "上传数据值" },
    { name: "上传时间", width: 160 },
    { name: "单位" },
    { name: "设置最小值" },
    { name: "设置最大值" },
    // { name: "值类型" },
    { name: "数据开启状态" },
  ],

  content: [
    // 无线水质设备 (4条)
    {
      id: "7",
      data: [
        "7",
        "无线水质设备",
        "7247-7",
        "724A00007GG8",
        "在线",
        "氨氮含量",
        "0.8",
        "2025-01-12 16:25:00",
        "山基物联",
        "0.8",
        "7.6",
        "开启",
      ],
    },
    {
      id: "12",
      data: [
        "12",
        "无线水质设备",
        "7247-12",
        "724A00012LL3",
        "离线",
        "总磷检测",
        "0.05",
        "2025-01-12 15:50:00",
        "山基物联",
        "0.05",
        "7.9",
        "关闭",
      ],
    },
    {
      id: "18",
      data: [
        "18",
        "无线水质设备",
        "7247-18",
        "724A00018RR9",
        "在线",
        "COD监测",
        "32.1",
        "2025-01-12 16:40:00",
        "山基物联",
        "32.1",
        "7.3",
        "开启",
      ],
    },
    {
      id: "23",
      data: [
        "23",
        "无线水质设备",
        "7247-23",
        "724A00023WW4",
        "在线",
        "叶绿素a",
        "12.5",
        "2025-01-12 16:55:00",
        "山基物联",
        "12.5",
        "7.4",
        "开启",
      ],
    },

    // 智能虫情测报灯设备 (3条)
    {
      id: "3",
      data: [
        "3",
        "智能虫情测报灯设备",
        "CQ-003",
        "CQ003A003FF6",
        "离线",
        "飞蛾监测",
        "0",
        "2025-01-12 15:30:00",
        "山基物联",
        "0",
        "22.0",
        "关闭",
      ],
    },
    {
      id: "10",
      data: [
        "10",
        "智能虫情测报灯设备",
        "CQ-010",
        "CQ010A010JJ0",
        "在线",
        "害虫密度",
        "156",
        "2025-01-12 16:35:00",
        "山基物联",
        "156",
        "26.2",
        "开启",
      ],
    },
    {
      id: "15",
      data: [
        "15",
        "智能虫情测报灯设备",
        "CQ-015",
        "CQ015A015OO5",
        "在线",
        "种类识别",
        "8",
        "2025-01-12 16:50:00",
        "山基物联",
        "8",
        "23.8",
        "开启",
      ],
    },

    // 电子秤 (5条)
    {
      id: "2",
      data: [
        "2",
        "电子秤",
        "DS-200",
        "DS200B200BB2",
        "在线",
        "实时称重",
        "5.75",
        "2025-01-12 16:03:00",
        "山基物联",
        "5.75",
        "20.5",
        "开启",
      ],
    },
    {
      id: "8",
      data: [
        "8",
        "电子秤",
        "DS-800",
        "DS800B800HH9",
        "在线",
        "累计重量",
        "124.8",
        "2025-01-12 16:28:00",
        "山基物联",
        "124.8",
        "21.0",
        "开启",
      ],
    },
    {
      id: "14",
      data: [
        "14",
        "电子秤",
        "DS-1400",
        "DS1400B1400NN4",
        "离线",
        "零点校准",
        "0.00",
        "2025-01-12 15:45:00",
        "山基物联",
        "0.00",
        "19.8",
        "关闭",
      ],
    },
    {
      id: "19",
      data: [
        "19",
        "电子秤",
        "DS-1900",
        "DS1900B1900SS9",
        "在线",
        "动态称重",
        "3.21",
        "2025-01-12 17:00:00",
        "山基物联",
        "3.21",
        "20.8",
        "开启",
      ],
    },
    {
      id: "22",
      data: [
        "22",
        "电子秤",
        "DS-2200",
        "DS2200B2200VV4",
        "在线",
        "超重报警",
        "0.0",
        "2025-01-12 17:10:00",
        "山基物联",
        "0.0",
        "20.2",
        "开启",
      ],
    },

    // 渔控仪-增氧机控制器 (2条)
    {
      id: "5",
      data: [
        "5",
        "渔控仪-增氧机控制器",
        "YK-005",
        "YK005C005XX5",
        "在线",
        "溶氧控制",
        "85",
        "2025-01-12 16:20:00",
        "山基物联",
        "85",
        "28.5",
        "开启",
      ],
    },
    {
      id: "16",
      data: [
        "16",
        "渔控仪-增氧机控制器",
        "YK-016",
        "YK016C016PP6",
        "离线",
        "定时增氧",
        "0",
        "2025-01-12 15:55:00",
        "山基物联",
        "0",
        "27.8",
        "关闭",
      ],
    },

    // 环境气味探测传感器 (6条)
    {
      id: "1",
      data: [
        "1",
        "环境气味探测传感器",
        "QW-001",
        "QW001D001AA1",
        "在线",
        "硫化氢",
        "0.002",
        "2025-01-12 16:00:00",
        "山基物联",
        "0.002",
        "25.5",
        "开启",
      ],
    },
    {
      id: "6",
      data: [
        "6",
        "环境气味探测传感器",
        "QW-006",
        "QW006D006FF6",
        "在线",
        "氨气浓度",
        "1.8",
        "2025-01-12 16:22:00",
        "山基物联",
        "1.8",
        "26.0",
        "开启",
      ],
    },
    {
      id: "9",
      data: [
        "9",
        "环境气味探测传感器",
        "QW-009",
        "QW009D009II9",
        "离线",
        "VOCs监测",
        "0.0",
        "2025-01-12 15:40:00",
        "山基物联",
        "0.0",
        "24.3",
        "关闭",
      ],
    },
    {
      id: "13",
      data: [
        "13",
        "环境气味探测传感器",
        "QW-013",
        "QW013D013MM3",
        "在线",
        "甲醛检测",
        "0.08",
        "2025-01-12 16:45:00",
        "山基物联",
        "0.08",
        "25.8",
        "开启",
      ],
    },
    {
      id: "17",
      data: [
        "17",
        "环境气味探测传感器",
        "QW-017",
        "QW017D017QQ7",
        "在线",
        "臭气强度",
        "2.5",
        "2025-01-12 16:58:00",
        "山基物联",
        "2.5",
        "26.5",
        "开启",
      ],
    },
    {
      id: "21",
      data: [
        "21",
        "环境气味探测传感器",
        "QW-021",
        "QW021D021UU1",
        "在线",
        "甲烷浓度",
        "0.05",
        "2025-01-12 17:05:00",
        "山基物联",
        "0.05",
        "25.2",
        "开启",
      ],
    },

    // 智能垃圾桶状态监测器 (4条)
    {
      id: "4",
      data: [
        "4",
        "智能垃圾桶状态监测器",
        "LJ-004",
        "LJ004E004ZZ4",
        "在线",
        "满溢监测",
        "92",
        "2025-01-12 16:12:00",
        "山基物联",
        "92",
        "30.5",
        "开启",
      ],
    },
    {
      id: "11",
      data: [
        "11",
        "智能垃圾桶状态监测器",
        "LJ-011",
        "LJ011E011KK1",
        "在线",
        "温度报警",
        "65",
        "2025-01-12 16:38:00",
        "山基物联",
        "65",
        "31.2",
        "开启",
      ],
    },
    {
      id: "20",
      data: [
        "20",
        "智能垃圾桶状态监测器",
        "LJ-020",
        "LJ020E020TT0",
        "离线",
        "GPS定位",
        "0",
        "2025-01-12 15:20:00",
        "山基物联",
        "0",
        "29.8",
        "关闭",
      ],
    },
    {
      id: "24",
      data: [
        "24",
        "智能垃圾桶状态监测器",
        "LJ-024",
        "LJ024E024YY4",
        "在线",
        "清运提醒",
        "88",
        "2025-01-12 17:15:00",
        "山基物联",
        "88",
        "30.0",
        "开启",
      ],
    },
  ],
});

const tabsData = ref({
  activeTab: 0,
  tabs: [
    { name: "首页", icon: "el-icon-s-home" },
    { name: "全部", icon: "el-icon-monitor" },
    { name: "无线水质", icon: "el-icon-connection" },
    { name: "智能虫情测报灯设备", icon: "el-icon-connection" },
    { name: "电子秤", icon: "el-icon-connection" },
    { name: "渔控仪-增氧机控制器", icon: "el-icon-connection" },
    { name: "环境气味探测传感器", icon: "el-icon-connection" },
    { name: "智能垃圾桶状态监测器", icon: "el-icon-connection" },
  ],
});

const filteredTableData = computed(() => {
  const activeTab = tabsData.value.activeTab;
  const activeTabName = tabsData.value.tabs[activeTab].name;

  if (activeTabName === "首页" || activeTabName === "全部") {
    return tableDataList.value.content;
  }

  return tableDataList.value.content.filter((row) => {
    const deviceTypeName = row.data[1];
    return deviceTypeName.includes(activeTabName);
  });
});

// 当前时间
const currentTime = ref("");
const currentDate = ref("");

// 数据卡片
const cards = ref([
  {
    title: "总用户数",
    value: "12,845",
    trend: 12.5,
    icon: "el-icon-user",
    color: "#409EFF",
  },
  {
    title: "今日新增",
    value: "356",
    trend: 8.2,
    icon: "el-icon-circle-plus",
    color: "#67C23A",
  },
  {
    title: "订单数量",
    value: "2,845",
    trend: -3.4,
    icon: "el-icon-s-order",
    color: "#E6A23C",
  },
  {
    title: "总收入",
    value: "¥128,450",
    trend: 22.1,
    icon: "el-icon-money",
    color: "#F56C6C",
  },
]);

// 表格数据
const tableData = ref([
  {
    id: 1,
    name: "水质仪1",
    status: "正常",
    value: "78%",
    rate: 2.5,
    time: "2026-01-16 10:23:45",
  },
  {
    id: 2,
    name: "水质仪2",
    status: "警告",
    value: "92%",
    rate: 5.8,
    time: "2026-01-16 10:23:42",
  },
  {
    id: 3,
    name: "虫情监测仪1",
    status: "正常",
    value: "45%",
    rate: -1.2,
    time: "2026-01-16 10:23:40",
  },
  {
    id: 4,
    name: "虫情监测仪2",
    status: "正常",
    value: "32%",
    rate: 0.8,
    time: "2026-01-16 10:23:38",
  },
  {
    id: 5,
    name: "电子秤1",
    status: "警告",
    value: "88%",
    rate: 12.5,
    time: "2026-01-16 10:23:35",
  },
  {
    id: 6,
    name: "电子秤2",
    status: "正常",
    value: "65%",
    rate: -0.5,
    time: "2026-01-16 10:23:30",
  },
  {
    id: 7,
    name: "增氧机1",
    status: "正常",
    value: "28%",
    rate: 1.2,
    time: "2026-01-16 10:23:25",
  },
  {
    id: 8,
    name: "增氧机2",
    status: "正常",
    value: "35%",
    rate: -2.8,
    time: "2026-01-16 10:23:20",
  },
  {
    id: 9,
    name: "增氧机3",
    status: "正常",
    value: "35%",
    rate: -2.8,
    time: "2026-01-16 10:23:20",
  },
  {
    id: 10,
    name: "增氧机4",
    status: "正常",
    value: "35%",
    rate: -2.8,
    time: "2026-01-16 10:23:20",
  },
]);

// 滚动信息
const scrollText =
  "系统运行正常 • 数据更新于2026-01-17 10:00:00 • 今日无异常报警 • 服务器负载正常";
const scrollPosition = ref(0);
let scrollInterval = null;

// 图表引用
const lineChart = ref(null);
const pieChart = ref(null);
const barChart = ref(null);
const gaugeChart = ref(null);

// 更新时间
const updateTime = () => {
  // console.log("updateTime()");
  const now = new Date();
  currentTime.value = now.toLocaleTimeString();
  currentDate.value =
    now.toLocaleDateString() +
    " " +
    ["星期日", "星期一", "星期二", "星期三", "星期四", "星期五", "星期六"][
      now.getDay()
    ];
};

// 初始化图表
const initCharts = () => {
  // console.log("initCharts(): lineChartInstance=", lineChartInstance.value);
  // // 折线图
  lineChartInstance.value.setOption({
    backgroundColor: "transparent",
    tooltip: {
      trigger: "axis",
    },
    legend: {
      data: ["访问量", "注册量"],
      textStyle: {
        color: "#fff",
      },
    },
    grid: {
      left: "3%",
      right: "4%",
      bottom: "3%",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      boundaryGap: false,
      data: [
        "00:00",
        "03:00",
        "06:00",
        "09:00",
        "12:00",
        "15:00",
        "18:00",
        "21:00",
        "24:00",
      ],
      axisLine: {
        lineStyle: {
          color: "#fff",
        },
      },
    },
    yAxis: {
      type: "value",
      axisLine: {
        lineStyle: {
          color: "#fff",
        },
      },
      splitLine: {
        lineStyle: {
          color: "rgba(255, 255, 255, 0.1)",
        },
      },
    },
    series: [
      {
        name: "访问量",
        type: "line",
        smooth: true,
        data: [120, 132, 101, 134, 290, 230, 210, 182, 191],
        itemStyle: {
          color: "#409EFF",
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: "rgba(64, 158, 255, 0.5)" },
            { offset: 1, color: "rgba(64, 158, 255, 0.1)" },
          ]),
        },
      },
      {
        name: "注册量",
        type: "line",
        smooth: true,
        data: [20, 32, 41, 34, 90, 130, 110, 82, 91],
        itemStyle: {
          color: "#67C23A",
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: "rgba(103, 194, 58, 0.5)" },
            { offset: 1, color: "rgba(103, 194, 58, 0.1)" },
          ]),
        },
      },
    ],
  });
  console.log("lineChartInstance.value.$refs=", lineChartInstance.value.$refs);

  // 饼图
  pieChartInstance.value.setOption({
    backgroundColor: "transparent",
    tooltip: {
      trigger: "item",
    },
    legend: {
      orient: "vertical",
      right: 10,
      top: "center",
      textStyle: {
        color: "#fff",
      },
    },
    series: [
      {
        name: "用户分布",
        type: "pie",
        radius: ["40%", "70%"],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 10,
          borderColor: "#0f2d5d",
          borderWidth: 2,
        },
        label: {
          show: false,
          position: "center",
        },
        emphasis: {
          label: {
            show: true,
            fontSize: "18",
            fontWeight: "bold",
            color: "#fff",
          },
        },
        labelLine: {
          show: false,
        },
        data: [
          { value: 1048, name: "华北", itemStyle: { color: "#409EFF" } },
          { value: 735, name: "华东", itemStyle: { color: "#67C23A" } },
          { value: 580, name: "华南", itemStyle: { color: "#E6A23C" } },
          { value: 484, name: "西部", itemStyle: { color: "#F56C6C" } },
          { value: 300, name: "其他", itemStyle: { color: "#909399" } },
        ],
      },
    ],
  });

  // 柱状图
  barChartInstance.value.setOption({
    backgroundColor: "transparent",
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "shadow",
      },
    },
    grid: {
      left: "3%",
      right: "4%",
      bottom: "3%",
      containLabel: true,
    },
    xAxis: {
      type: "value",
      axisLine: {
        lineStyle: {
          color: "#fff",
        },
      },
      splitLine: {
        lineStyle: {
          color: "rgba(255, 255, 255, 0.1)",
        },
      },
    },
    yAxis: {
      type: "category",
      data: ["CPU", "内存", "磁盘", "网络", "GPU"],
      axisLine: {
        lineStyle: {
          color: "#fff",
        },
      },
    },
    series: [
      {
        name: "使用率",
        type: "bar",
        data: [78, 65, 45, 32, 88],
        itemStyle: {
          color: function (params) {
            const colorList = [
              "#409EFF",
              "#67C23A",
              "#E6A23C",
              "#F56C6C",
              "#909399",
            ];
            return colorList[params.dataIndex];
          },
          borderRadius: [0, 4, 4, 0],
        },
      },
    ],
  });

  // 仪表盘
  gaugeChartInstance.value.setOption({
    backgroundColor: "transparent",
    series: [
      {
        type: "gauge",
        progress: {
          show: true,
          width: 18,
          itemStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: "#67C23A" },
                { offset: 0.7, color: "#E6A23C" },
                { offset: 1, color: "#F56C6C" },
              ],
            },
          },
        },
        axisLine: {
          lineStyle: {
            width: 18,
            color: [[1, "rgba(255, 255, 255, 0.1)"]],
          },
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          length: 15,
          lineStyle: {
            width: 2,
            color: "#fff",
          },
        },
        axisLabel: {
          distance: 25,
          color: "#fff",
          fontSize: 14,
        },
        anchor: {
          show: true,
          showAbove: true,
          size: 25,
          itemStyle: {
            borderWidth: 10,
            borderColor: "#fff",
          },
        },
        title: {
          show: false,
        },
        detail: {
          valueAnimation: true,
          fontSize: 30,
          offsetCenter: [0, "70%"],
          color: "#fff",
          formatter: "{value}%",
        },
        data: [
          {
            value: 78.5,
          },
        ],
      },
    ],
  });

  window.addEventListener("error", resizeObserverErrHandler);
  window.addEventListener("resize", handleResize);
};

const handleResize = debounce(() => {
  console.log("handleResize()");

  if (lineChartInstance.value) {
    lineChartInstance.value.resize();
    pieChartInstance.value.resize();
    barChartInstance.value.resize();
    gaugeChartInstance.value.resize();
  }
}, 100);

const resizeObserverErrHandler = (err) => {
  console.log("resizeObserverErrHandler(), err=", err);

  if (err.message.includes("ResizeObserver")) {
    return;
  }
  console.error(err);
};

// 滚动信息动画
const startScroll = () => {
  console.log("startScroll()");
  scrollInterval = setInterval(() => {
    scrollPosition.value -= 1;
    if (scrollPosition.value < -scrollText.length * 10) {
      scrollPosition.value = 0;
    }
  }, 50);
};

const lineChartInstance = ref(null);
const pieChartInstance = ref(null);
const barChartInstance = ref(null);
const gaugeChartInstance = ref(null);

const tabChange = (index, name) => {
  tabsData.value.activeTab = index;
};
// 组件挂载时初始化
onMounted(() => {
  console.log("onMounted()");

  nextTick(() => {
    console.log("onMount(): lineChart=", lineChart.value);
    lineChartInstance.value = echarts.init(lineChart.value);
    pieChartInstance.value = echarts.init(pieChart.value);
    barChartInstance.value = echarts.init(barChart.value);
    gaugeChartInstance.value = echarts.init(gaugeChart.value);

    updateTime();
    setInterval(updateTime, 1000);

    initCharts();
    startScroll();

    // 模拟实时数据更新
    setInterval(() => {
      // console.log("setInterval(): lineChartInstance=", lineChartInstance.value);

      // 更新卡片数据
      cards.value.forEach((card) => {
        const change = Math.random() * 10 - 5;
        if (card.title === "总用户数") {
          const num = parseInt(card.value.replace(/,/g, ""));
          card.value = (num + Math.floor(Math.random() * 10)).toLocaleString();
        } else if (card.title === "今日新增") {
          const num = parseInt(card.value);
          card.value = (num + Math.floor(Math.random() * 5 - 2)).toString();
        } else if (card.title === "订单数量") {
          const num = parseInt(card.value.replace(/,/g, ""));
          card.value = (
            num + Math.floor(Math.random() * 20 - 10)
          ).toLocaleString();
        }
        card.trend = parseFloat((card.trend + change / 10).toFixed(1));
      });

      // 更新表格数据
      tableData.value.forEach((row) => {
        row.value = Math.floor(Math.random() * 30 + 20) + "%";
        row.rate = parseFloat((Math.random() * 10 - 5).toFixed(1));
        const now = new Date();
        row.time = now.toLocaleDateString() + " " + now.toLocaleTimeString();
      });
    }, 5000);
  });
});

// 组件卸载前清除定时器
onBeforeUnmount(() => {
  console.log("onBeforeUnmount()");
  window.removeEventListener("resize", resizeObserverErrHandler);
  window.removeEventListener("resize", handleResize);
  if (scrollInterval) {
    clearInterval(scrollInterval);
  }
});
</script>

<style scoped>
/* 基础样式 */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.dashboard-container {
  width: 100%;
  height: 100vh;
  background: linear-gradient(to right, #0f1c3e, #0f2d5d);
  color: #fff;
  font-family: "Arial", sans-serif;
  overflow: hidden;
  padding: 20px;
  position: relative;
}

/* 3D标题效果 */
.title-container {
  position: relative;
  text-align: center;
  margin-bottom: 20px;
}

.title-3d {
  font-size: 42px;
  font-weight: bold;
  color: #fff;
  text-shadow: 0 5px 15px rgba(0, 150, 255, 0.5);
  transform: perspective(500px) rotateX(10deg);
  letter-spacing: 2px;
  position: relative;
  z-index: 1;
}

.title-shadow {
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%) perspective(500px) rotateX(60deg) scaleY(0.8);
  width: 50%;
  height: 20px;
  background: radial-gradient(
    ellipse at center,
    rgba(0, 150, 255, 0.5) 0%,
    rgba(0, 0, 0, 0) 70% /* transparent 100% */
  );
  filter: blur(5px);
  z-index: 0;
  will-change: transform;
}

/* 时间显示 */
.time-display {
  position: absolute;
  top: 20px;
  right: 20px;
  text-align: right;
}

.time {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 5px;
}

.date {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.7);
}

/* 数据卡片 */
.data-cards {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  gap: 10px;
}

.button {
  flex: 1;
  margin: 0 10px;
  background: rgba(16, 42, 90, 0.7);
  border-radius: 8px;
  padding: 15px;
  display: flex;
  align-items: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  border: 1px solid rgba(64, 158, 255, 0.2);
  height: 10px;
  margin-bottom: 10px;
  cursor: pointer;
}

.card {
  flex: 1;
  background: rgba(16, 42, 90, 0.7);
  border-radius: 8px;
  padding: 15px;
  display: flex;
  align-items: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  border: 1px solid rgba(64, 158, 255, 0.2);
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.4);
  border-color: rgba(64, 158, 255, 0.5);
}

.card-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  font-size: 24px;
  color: #fff;
}

.card-content {
  flex: 1;
}

.card-title {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 5px;
}

.card-value {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 5px;
}

.card-trend {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
}

/* 主内容区 */
.main-content {
  display: flex;
  height: calc(100vh - 320px);
  gap: 10px;
}

.left-section,
.right-section {
  width: 25%;
  display: flex;
  flex-direction: column;
}

.center-section {
  width: calc(50% + 8px);
}

.chart-container {
  background: rgba(16, 42, 90, 0.7);
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 10px;
  height: 50%;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(64, 158, 255, 0.2);
}

.chart-title {
  font-size: 16px;
  margin-bottom: 10px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: bold;
}

.chart {
  width: 100%;
  height: 200px;
}

.table-container {
  background: rgba(16, 42, 90, 0.7);
  border-radius: 8px;
  padding: 15px;
  /* height: calc(100% - 10px); */
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(64, 158, 255, 0.2);
}

.table-title {
  font-size: 16px;
  margin-bottom: 10px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: bold;
}

/* 滚动信息 */
.scroll-info {
  width: 100%;
  height: 30px;
  background: rgba(16, 42, 90, 0.7);
  border-radius: 4px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(64, 158, 255, 0.2);
}

.scroll-content {
  position: absolute;
  white-space: nowrap;
  line-height: 30px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
}

/* 响应式调整 */
@media (max-width: 1200px) {
  .main-content {
    flex-direction: column;
    height: auto;
  }

  .left-section,
  .center-section,
  .right-section {
    width: 100%;
    margin: 0 0 10px 0;
  }

  .chart-container {
    height: 300px;
  }

  /* 新增的页签样式 */
  .tab-container {
    display: flex;
    background: rgba(16, 42, 90, 0.7);
    border-radius: 8px;
    margin-bottom: 20px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(64, 158, 255, 0.2);
    overflow: hidden;
  }

  .tab-item {
    flex: 1;
    padding: 12px 0;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s;
    color: rgba(255, 255, 255, 0.7);
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .tab-item i {
    margin-right: 8px;
    font-size: 16px;
  }

  .tab-item.active {
    background: rgba(64, 158, 255, 0.3);
    color: #fff;
    font-weight: bold;
    border-bottom: 2px solid #409eff;
  }

  .tab-item:hover:not(.active) {
    background: rgba(64, 158, 255, 0.1);
    color: rgba(255, 255, 255, 0.9);
  }
}
</style>
